/**
 * Zugriff auf die Edge Function admin-users.
 *
 * Liegt hier und nicht in einer der Seiten, weil zwei Stellen sie brauchen:
 * die Nutzerliste (UsersPage) und der Detaildialog (UserDetailModal). Beide
 * duerfen sich beim Sperren nicht unterscheiden - Sperre und Anzeige haengen
 * an derselben Function.
 */

import { getSupabaseClient } from '@parentpath/supabase-client';

// Der Aufruf muss das JWT des eingeloggten Admins mitschicken; die Function
// identifiziert den Aufrufer daraus und prueft is_admin() in der Datenbank.
const authClient = getSupabaseClient();

/**
 * Ruft admin-users auf und hebt die Fehlermeldung aus dem Response-Body.
 * supabase-js liefert bei non-2xx nur "Edge Function returned a non-2xx status
 * code"; die eigentliche Ursache steht im Body als { error: "..." }.
 */
export async function callAdminUsers<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await authClient.functions.invoke('admin-users', { body });

  if (error) {
    let message = error.message;
    const response = (error as { context?: Response }).context;
    if (response && typeof response.json === 'function') {
      try {
        const payload = await response.json();
        if (payload?.error) message = payload.error;
      } catch {
        // Body nicht lesbar - es bleibt bei der generischen Meldung.
      }
    }
    throw new Error(message);
  }

  return data as T;
}

/**
 * Uebersetzt die englischen Fehler der 'ban'-Action in eine Meldung, die im
 * Panel etwas aussagt. Der Teilerfolg (Sperre greift, Profil nicht
 * geschrieben) bekommt bewusst einen eigenen Text: der Admin muss wissen,
 * dass der Nutzer bereits ausgesperrt ist, auch wenn die Liste es noch nicht
 * zeigt - und dass ein zweiter Klick den Rest nachholt.
 */
export function describeBanError(message: string): string {
  const normalized = message.toLowerCase();

  if (normalized.includes('cannot ban your own account')) {
    return 'Du kannst deinen eigenen Account nicht sperren. Das muss ein anderer Admin uebernehmen.';
  }
  if (normalized.includes('cannot ban admin users')) {
    return 'Dieser Account hat Admin-Rechte und kann nicht gesperrt werden. Die Admin-Rolle muss vorher in der Datenbank entzogen werden.';
  }
  if (normalized.includes('auth ban state was updated')) {
    return 'Die Sperre greift bereits, aber der Profilstatus konnte nicht aktualisiert werden. Bitte die Aktion wiederholen.';
  }
  if (normalized.includes('access denied') || normalized.includes('admin privileges')) {
    return 'Keine Berechtigung: Zum Sperren werden Admin-Rechte benoetigt.';
  }
  if (normalized.includes('unauthorized') || normalized.includes('no authorization header')) {
    return 'Die Sitzung ist abgelaufen. Bitte neu anmelden und es noch einmal versuchen.';
  }
  if (normalized.includes('valid user_id')) {
    return 'Ungueltige Benutzer-ID - der Account konnte nicht zugeordnet werden.';
  }
  if (normalized.includes('not configured')) {
    return 'Die Edge Function admin-users ist nicht vollstaendig konfiguriert. Bitte im Backend pruefen.';
  }

  return `Der Sperrstatus konnte nicht geaendert werden: ${message}`;
}
